import axios from 'axios';
import { toast } from 'sonner';

const BACKEND = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND}/api`;

export const api = axios.create({
  baseURL: API,
  withCredentials: true,
});

/** PIN unlock token (returned from /parent/pin/verify) is kept in sessionStorage
 *  so it survives navigation within a single browser tab but does NOT persist
 *  after the tab is closed. The interceptor below attaches it to any request
 *  hitting a PIN-gated endpoint (OCR + book creation). */
export const PIN_UNLOCK_KEY = 'pinUnlockToken';
export const setPinUnlockToken = (t) => {
  if (t) sessionStorage.setItem(PIN_UNLOCK_KEY, t);
  else sessionStorage.removeItem(PIN_UNLOCK_KEY);
};
export const getPinUnlockToken = () => sessionStorage.getItem(PIN_UNLOCK_KEY) || '';

api.interceptors.request.use((config) => {
  const url = config.url || '';
  // Attach the PIN unlock header for PIN-gated endpoints:
  //   POST /ocr, POST /pdf/extract, POST /books (create),
  //   POST /books/{id}/pages (append pages)
  // Previously the regex only matched /books at end which silently dropped
  // the header on /books/{id}/pages → backend 403 with no way to recover.
  // Iter-51.44 — also attach the header for /quiz-runs/{id} PATCH
  // (Trial toggle) — that endpoint is PIN-gated like the other
  // parent-facing content edits.
  if (/\/ocr(\?|$)/.test(url) || /\/books(\/|\?|$)/.test(url) || /\/pdf\//.test(url) || /\/sessions\//.test(url) || /\/quiz-runs\//.test(url)) {
    const t = getPinUnlockToken();
    if (t) config.headers['X-Pin-Unlock'] = t;
  }
  return config;
});

/** Global 404 handler for stale child/book IDs. Clears activeChild &
 *  redirects to /profiles or /books rather than throwing a runtime error. */
api.interceptors.response.use(
  (r) => r,
  (error) => {
    const status = error?.response?.status;
    const url = error?.config?.url || '';
    // Iter-49: trial-expired → route to /trial-ended. Backend also
    // uses 402 for the family-profile-limit error which the UI needs
    // to surface inline (a modal, not a page nav), so only redirect
    // when the detail code is explicitly `trial_expired`.
    if (status === 402) {
      const code = error?.response?.data?.detail?.code;
      if (code === 'trial_expired' && typeof window !== 'undefined'
          && !window.location.pathname.startsWith('/trial-ended')) {
        window.location.replace('/trial-ended');
        return new Promise(() => {});
      }
      // Iter-51.58 — surface plan-limit rejections (page cap / monthly
      // upload cap) as a global toast so the upload flow can't silently
      // swallow them. The detail is a plain string from the backend.
      try {
        const detail = error?.response?.data?.detail;
        if (typeof detail === 'string' && detail && code !== 'trial_expired') {
          toast.error(detail, { duration: 8000 });
        }
      } catch {}
    }
    if (status === 404) {
      // Stale book — go back to library
      if (/\/books\/[^/]+/.test(url) && !url.endsWith('/books')) {
        if (typeof window !== 'undefined' && !window.location.pathname.startsWith('/books')) {
          window.location.replace('/books');
        }
        return new Promise(() => {}); // swallow — never resolves so the caller's .then never runs
      }
      // Stale child — reset profile selection
      if (/\/children\/[^/]+/.test(url)) {
        try { localStorage.removeItem('activeChild'); } catch {}
        if (typeof window !== 'undefined' && window.location.pathname !== '/profiles') {
          window.location.replace('/profiles');
        }
        return new Promise(() => {});
      }
    }
    return Promise.reject(error);
  }
);
